import { OrganizationInfo, OrganizationPlanType, UserInfo } from '../types';

/**
 * Stripe customer for profile billing: user’s customer for Pro/Advanced, or the org’s
 * customer when on an org-credit workspace (admins receive `organizationInfo.stripeCustomerID`).
 */
export function getStripeCustomerIdForBilling(
  userInfo: Pick<UserInfo, 'stripeCustomerID'>,
  organizationInfo: OrganizationInfo | undefined | null,
): string {
  const orgCredits = isOrgCreditsWorkspace(organizationInfo);
  return (
    userInfo.stripeCustomerID?.trim() ||
    (orgCredits ? organizationInfo?.stripeCustomerID?.trim() : undefined) ||
    ''
  );
}

/**
 * True when the org is in an active Stripe free trial (`inStripeFreeTrialPeriod` and trial
 * end still in the future). Stale `inStripeFreeTrialPeriod` after trial lapse is ignored.
 */
export function isActiveFreeTrial(
  organizationInfo: OrganizationInfo | undefined | null,
): boolean {
  if (organizationInfo?.inStripeFreeTrialPeriod !== true) {
    return false;
  }
  const trialEnd = organizationInfo.nextBillingDate?.trim();
  if (!trialEnd) {
    return false;
  }
  const trialEndMs = Date.parse(trialEnd);
  if (Number.isNaN(trialEndMs)) {
    return false;
  }
  return trialEndMs > Date.now();
}

/** Stripe trial flag set but trial end date is missing or in the past. */
export function isExpiredFreeTrial(
  organizationInfo: OrganizationInfo | undefined | null,
): boolean {
  if (!isOrgCreditsWorkspace(organizationInfo)) {
    return false;
  }
  if (organizationInfo?.inStripeFreeTrialPeriod !== true) {
    return false;
  }
  return !isActiveFreeTrial(organizationInfo);
}

/** Paid org-credit subscription (not in an active Stripe trial). */
export function hasActivePaidSubscription(
  organizationInfo: OrganizationInfo | undefined | null,
): boolean {
  if (!isOrgCreditsWorkspace(organizationInfo)) {
    return false;
  }
  if (isActiveFreeTrial(organizationInfo)) {
    return false;
  }
  return organizationInfo!.inStripeFreeTrialPeriod !== true;
}

/** Workspace/API orgs billed on org credits (monthly refresh & balance), not personal Spins. */
export function isOrgCreditsWorkspace(
  organizationInfo: OrganizationInfo | undefined | null,
): boolean {
  return (
    organizationInfo != null &&
    typeof organizationInfo.monthlyCreditRefreshAmount === 'number' &&
    organizationInfo.monthlyCreditRefreshAmount > 0
  );
}

/** Total credits available for billing (API may also send `availableCredits`). */
export function getOrgAvailableCredits(
  organizationInfo: OrganizationInfo | undefined | null,
): number {
  if (!organizationInfo) {
    return 0;
  }
  return (
    organizationInfo.availableCredits ??
    (organizationInfo.currentCreditBalance ?? 0) +
    (organizationInfo.purchasedCredits ?? 0)
  );
}

/**
 * Workspace org-credit plans: true when available credits are **strictly below** 10% of the
 * monthly included amount (includes zero credits). Used to prompt the purchase-credits modal.
 */
export function isOrgCreditsBelowTenPercentOfMonthly(
  organizationInfo: OrganizationInfo | undefined | null,
): boolean {
  if (!isOrgCreditsWorkspace(organizationInfo)) {
    return false;
  }
  const monthly = organizationInfo!.monthlyCreditRefreshAmount!;
  const available = getOrgAvailableCredits(organizationInfo);
  return available <= monthly * 0.1;
}

/** Rounds to one decimal place and formats for display (available credits, refresh amounts). */
export function formatOrgCreditsForDisplay(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

const ORG_PLAN_LABELS: Record<OrganizationPlanType, string> = {
  api_standard: 'API Standard',
  custom: 'Custom',
  individual_basic: 'Individual Basic',
  individual_pro: 'Individual Pro',
  team_standard: 'Team Standard',
  team_pro: 'Team Pro',
  team_scale: 'Team Scale',
  platform: 'Platform',
  legacy_free: 'Free',
  legacy_pro: 'Professional',
  legacy_unlimited: 'Unlimited',
};

export function formatOrganizationPlanType(
  planType: OrganizationPlanType | undefined,
): string {
  if (!planType) {
    return 'Workspace';
  }
  return ORG_PLAN_LABELS[planType] ?? planType;
}

/** Org plans that do not show the “Cartwheel API keys” block on My Team (legacy). */
const ORG_PLAN_TYPES_HIDING_API_KEYS_UI: ReadonlyArray<OrganizationPlanType> = [
  'legacy_free',
  'legacy_pro',
  'legacy_unlimited',
];

export function orgPlanHidesCartwheelApiKeysSection(
  planType: OrganizationPlanType | undefined,
): boolean {
  return (
    planType !== undefined &&
    ORG_PLAN_TYPES_HIDING_API_KEYS_UI.includes(planType)
  );
}

/** Org plans where users cannot manage Cartwheel API keys in profile (show upgrade CTA instead). */
const ORG_PLAN_TYPES_BLOCKING_API_KEY_MANAGEMENT: ReadonlyArray<OrganizationPlanType> = [
  'legacy_free',
  'legacy_pro',
  'legacy_unlimited',
];

export function orgPlanBlocksCartwheelApiKeyManagement(
  planType: OrganizationPlanType | undefined,
): boolean {
  return (
    planType !== undefined &&
    ORG_PLAN_TYPES_BLOCKING_API_KEY_MANAGEMENT.includes(planType)
  );
}

export function getCartwheelApiKeysUpgradeMessaging(
  organizationPlanType: OrganizationPlanType | undefined,
): { planLabel: string; pricingPath: '/pricing' | '/pricing/team-plans' } {
  if (organizationPlanType === 'team_standard') {
    return {
      planLabel: 'Team Pro',
      pricingPath: '/pricing/team-plans',
    };
  }
  return {
    planLabel: 'Individual Pro',
    pricingPath: '/pricing',
  };
}
